import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const TablesManagement = () => {
  const { restaurantId } = useParams();
  const navigate = useNavigate();
  const [restaurant, setRestaurant] = useState(null);
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showSingleModal, setShowSingleModal] = useState(false);
  const [qrModalTable, setQrModalTable] = useState(null);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    fetchAll();
  }, [restaurantId]);

  const fetchAll = async () => {
    try {
      const [r, t] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/restaurants/${restaurantId}`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/tables/restaurant/${restaurantId}`, { withCredentials: true }),
      ]);
      setRestaurant(r.data);
      setTables(t.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Yüklənmə xətası');
    } finally {
      setLoading(false);
    }
  };

  const bulkCreate = async (startNumber, count, prefix) => {
    setCreating(true);
    setError('');
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/tables/bulk`,
        { restaurant_id: restaurantId, start_number: startNumber, count, name_prefix: prefix || null },
        { withCredentials: true }
      );
      await fetchAll();
      setShowBulkModal(false);
      if (data.skipped > 0) {
        alert(`✅ ${data.created} masa yaradıldı. ${data.skipped} masa artıq mövcud olduğu üçün ötürüldü.`);
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Yaratma xətası');
    } finally {
      setCreating(false);
    }
  };

  const createSingle = async (number, name) => {
    setCreating(true);
    setError('');
    try {
      await axios.post(
        `${BACKEND_URL}/api/tables`,
        { restaurant_id: restaurantId, number, name: name || null },
        { withCredentials: true }
      );
      await fetchAll();
      setShowSingleModal(false);
    } catch (e) {
      setError(e.response?.data?.detail || 'Yaratma xətası');
    } finally {
      setCreating(false);
    }
  };

  const deleteTable = async (tableId, number) => {
    if (!window.confirm(`Masa #${number} silinsin?`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/tables/${tableId}`, { withCredentials: true });
      setTables((prev) => prev.filter((t) => t.id !== tableId));
    } catch (e) {
      alert('Silmə xətası: ' + (e.response?.data?.detail || e.message));
    }
  };

  const updateTable = async (tableId, updates) => {
    try {
      const { data } = await axios.patch(
        `${BACKEND_URL}/api/tables/${tableId}`,
        updates,
        { withCredentials: true }
      );
      setTables((prev) => prev.map((t) => (t.id === tableId ? data : t)));
    } catch (e) {
      alert('Yenilənmə xətası: ' + (e.response?.data?.detail || e.message));
    }
  };

  const downloadQR = (table) => {
    const link = document.createElement('a');
    link.href = table.qr_code;
    link.download = `${restaurant.name.replace(/[^a-zA-Z0-9]/g, '_')}_Masa_${table.number}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const printAll = () => {
    window.open(`/restaurant/${restaurantId}/tables/print`, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  if (!restaurant) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-red-600">Restoran tapılmadı</p>
      </div>
    );
  }

  const activeCount = tables.filter((t) => t.is_active).length;

  return (
    <div className="min-h-screen bg-stone-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        <button
          onClick={() => navigate(`/restaurant/${restaurantId}`)}
          className="text-emerald-600 hover:underline mb-4"
          data-testid="tables-back-btn"
        >← Restoran ayarları</button>

        <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6 mb-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-stone-900" data-testid="tables-title">🪑 Masa İdarəetməsi</h1>
              <p className="text-sm text-stone-500 mt-1">{restaurant.name}</p>
            </div>
            <div className="flex items-center gap-4 text-sm">
              <div className="text-center">
                <div className="text-2xl font-bold text-stone-900">{tables.length}</div>
                <div className="text-xs text-stone-500 uppercase">Cəmi</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-emerald-600">{activeCount}</div>
                <div className="text-xs text-stone-500 uppercase">Aktiv</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-stone-400">{tables.length - activeCount}</div>
                <div className="text-xs text-stone-500 uppercase">Passiv</div>
              </div>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap gap-2">
            <button
              onClick={() => setShowBulkModal(true)}
              className="px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-semibold text-sm"
              data-testid="bulk-create-btn"
            >＋ Bulk masa yarat</button>
            <button
              onClick={() => setShowSingleModal(true)}
              className="px-4 py-2.5 bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-xl font-semibold text-sm"
              data-testid="single-create-btn"
            >＋ Tək masa</button>
            {tables.length > 0 && (
              <button
                onClick={printAll}
                className="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-semibold text-sm"
                data-testid="print-all-btn"
              >🖨️ Hamısını çap et</button>
            )}
            {tables.length > 0 && (
              <button
                onClick={() => navigate(`/restaurant/${restaurantId}/nfc`)}
                className="px-4 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-semibold text-sm"
                data-testid="nfc-codes-btn"
              >📡 NFC Kodları</button>
            )}
          </div>

          {error && (
            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-testid="tables-error">
              ⚠️ {error}
            </div>
          )}
        </div>

        {/* Tables grid */}
        {tables.length === 0 ? (
          <div className="bg-white rounded-2xl border-2 border-dashed border-stone-300 p-12 text-center">
            <div className="text-5xl mb-3">🪑</div>
            <p className="text-stone-600 font-semibold">Hələ masa yaradılmayıb</p>
            <p className="text-sm text-stone-400 mt-1">Yuxarıdakı &quot;Bulk masa yarat&quot; düyməsi ilə başlayın</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4" data-testid="tables-grid">
            {tables.map((table) => (
              <div
                key={table.id}
                className={`bg-white rounded-2xl border-2 p-4 transition hover:shadow-lg ${
                  table.is_active ? 'border-stone-200' : 'border-red-200 bg-red-50/30'
                }`}
                data-testid={`table-card-${table.id}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="text-2xl font-black text-stone-900">#{table.number}</div>
                    {editingId === table.id ? (
                      <input
                        type="text"
                        defaultValue={table.name || ''}
                        autoFocus
                        onBlur={(e) => {
                          updateTable(table.id, { name: e.target.value });
                          setEditingId(null);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            updateTable(table.id, { name: e.target.value });
                            setEditingId(null);
                          } else if (e.key === 'Escape') {
                            setEditingId(null);
                          }
                        }}
                        className="text-xs px-2 py-0.5 border border-stone-300 rounded mt-1 w-full"
                      />
                    ) : (
                      <button
                        onClick={() => setEditingId(table.id)}
                        className="text-xs text-stone-500 hover:text-emerald-600 italic"
                        data-testid={`table-name-${table.id}`}
                      >
                        {table.name || '+ Ad əlavə et'}
                      </button>
                    )}
                  </div>
                  <button
                    onClick={() => updateTable(table.id, { is_active: !table.is_active })}
                    className={`w-9 h-5 rounded-full transition relative ${
                      table.is_active ? 'bg-emerald-500' : 'bg-stone-300'
                    }`}
                    data-testid={`table-toggle-${table.id}`}
                  >
                    <span
                      className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition ${
                        table.is_active ? 'translate-x-4' : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                </div>

                <img
                  src={table.qr_code}
                  alt={`QR Masa ${table.number}`}
                  className={`w-full aspect-square object-contain bg-white border border-stone-200 rounded-lg cursor-pointer ${!table.is_active ? 'grayscale opacity-60' : ''}`}
                  onClick={() => setQrModalTable(table)}
                  data-testid={`table-qr-img-${table.id}`}
                />

                <div className="mt-2 grid grid-cols-2 gap-1.5">
                  <button
                    onClick={() => downloadQR(table)}
                    className="px-2 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-[11px] font-semibold"
                    data-testid={`table-download-${table.id}`}
                  >⬇ PNG</button>
                  <button
                    onClick={() => deleteTable(table.id, table.number)}
                    className="px-2 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-[11px] font-semibold"
                    data-testid={`table-delete-${table.id}`}
                  >🗑 Sil</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* QR Modal */}
      {qrModalTable && (
        <QrModal table={qrModalTable} restaurant={restaurant} onClose={() => setQrModalTable(null)} onDownload={() => downloadQR(qrModalTable)} />
      )}

      {/* Bulk modal */}
      {showBulkModal && (
        <BulkModal
          existingMax={tables.reduce((m, t) => Math.max(m, t.number), 0)}
          creating={creating}
          onClose={() => setShowBulkModal(false)}
          onSubmit={bulkCreate}
        />
      )}

      {/* Single modal */}
      {showSingleModal && (
        <SingleModal
          existingMax={tables.reduce((m, t) => Math.max(m, t.number), 0)}
          creating={creating}
          onClose={() => setShowSingleModal(false)}
          onSubmit={createSingle}
        />
      )}
    </div>
  );
};

const QrModal = ({ table, restaurant, onClose, onDownload }) => (
  <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
    <div className="bg-white rounded-3xl p-8 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
      <div className="text-center mb-4">
        <p className="text-xs uppercase font-bold text-stone-500 tracking-wider">{restaurant.name}</p>
        <h2 className="text-3xl font-black text-stone-900 mt-1">Masa #{table.number}</h2>
        {table.name && <p className="text-sm text-stone-600 mt-0.5">{table.name}</p>}
      </div>
      <img src={table.qr_code} alt="QR" className="w-full aspect-square object-contain bg-white border-4 border-stone-900 rounded-2xl mb-4" />
      <p className="text-center text-xs text-stone-500 mb-4">Müştərilər bu QR-ni telefonla skan edib menyu görür və sifariş verir</p>
      <div className="flex gap-2">
        <button onClick={onClose} className="flex-1 px-4 py-2.5 bg-stone-100 hover:bg-stone-200 rounded-xl font-semibold text-sm">Bağla</button>
        <button onClick={onDownload} className="flex-1 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-semibold text-sm" data-testid="qr-modal-download">⬇ PNG yüklə</button>
      </div>
    </div>
  </div>
);

const BulkModal = ({ existingMax, creating, onClose, onSubmit }) => {
  const [count, setCount] = useState(10);
  const [startNum, setStartNum] = useState(existingMax + 1);
  const [prefix, setPrefix] = useState('');

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="bulk-modal">
      <div className="bg-white rounded-2xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-xl font-bold text-stone-900 mb-4">📦 Bulk masa yarat</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-stone-700 mb-1.5">Neçə masa?</label>
            <div className="grid grid-cols-4 gap-2">
              {[5, 10, 20, 50].map((n) => (
                <button
                  key={n}
                  onClick={() => setCount(n)}
                  className={`py-2 rounded-lg font-bold text-sm border-2 ${count === n ? 'border-emerald-600 bg-emerald-50 text-emerald-700' : 'border-stone-200 text-stone-600'}`}
                  data-testid={`bulk-preset-${n}`}
                >{n}</button>
              ))}
            </div>
            <input
              type="number"
              min="1"
              max="100"
              value={count}
              onChange={(e) => setCount(parseInt(e.target.value, 10) || 1)}
              className="mt-2 w-full px-4 py-2 border border-stone-300 rounded-xl text-sm"
              data-testid="bulk-count-input"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-stone-700 mb-1.5">Başlama nömrəsi</label>
            <input
              type="number"
              min="1"
              max="500"
              value={startNum}
              onChange={(e) => setStartNum(parseInt(e.target.value, 10) || 1)}
              className="w-full px-4 py-2 border border-stone-300 rounded-xl text-sm"
              data-testid="bulk-start-input"
            />
            <p className="text-xs text-stone-500 mt-1">
              Yaradılacaq: Masa #{startNum} → Masa #{startNum + count - 1}
            </p>
          </div>
          <div>
            <label className="block text-sm font-semibold text-stone-700 mb-1.5">Prefix (istəyə bağlı)</label>
            <input
              type="text"
              value={prefix}
              onChange={(e) => setPrefix(e.target.value)}
              placeholder="məs: T, VIP, Terras"
              className="w-full px-4 py-2 border border-stone-300 rounded-xl text-sm"
              data-testid="bulk-prefix-input"
            />
            {prefix && (
              <p className="text-xs text-stone-500 mt-1">Adı: &quot;{prefix}{startNum}&quot;, &quot;{prefix}{startNum + 1}&quot;, ...</p>
            )}
          </div>
        </div>
        <div className="mt-6 flex gap-2">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 bg-stone-100 hover:bg-stone-200 rounded-xl font-semibold text-sm">Ləğv</button>
          <button
            onClick={() => onSubmit(startNum, count, prefix)}
            disabled={creating || count < 1}
            className="flex-1 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-semibold text-sm disabled:opacity-50"
            data-testid="bulk-submit-btn"
          >{creating ? 'Yaradılır...' : 'Yarat'}</button>
        </div>
      </div>
    </div>
  );
};

const SingleModal = ({ existingMax, creating, onClose, onSubmit }) => {
  const [number, setNumber] = useState(existingMax + 1);
  const [name, setName] = useState('');

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="single-modal">
      <div className="bg-white rounded-2xl p-6 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-xl font-bold text-stone-900 mb-4">🪑 Tək masa yarat</h3>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-semibold text-stone-700 mb-1.5">Nömrə</label>
            <input
              type="number"
              min="1"
              max="500"
              value={number}
              onChange={(e) => setNumber(parseInt(e.target.value, 10) || 1)}
              className="w-full px-4 py-2.5 border border-stone-300 rounded-xl"
              data-testid="single-number-input"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-stone-700 mb-1.5">Ad (istəyə bağlı)</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="məs: VIP Kabin, Terras 1"
              className="w-full px-4 py-2.5 border border-stone-300 rounded-xl"
              data-testid="single-name-input"
            />
          </div>
        </div>
        <div className="mt-6 flex gap-2">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 bg-stone-100 hover:bg-stone-200 rounded-xl font-semibold text-sm">Ləğv</button>
          <button
            onClick={() => onSubmit(number, name)}
            disabled={creating}
            className="flex-1 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-semibold text-sm disabled:opacity-50"
            data-testid="single-submit-btn"
          >{creating ? 'Yaradılır...' : 'Yarat'}</button>
        </div>
      </div>
    </div>
  );
};

/**
 * Print-ready page (4 QR codes per A4 page, suitable for cutting & placing on tables).
 */
export const TablesPrint = () => {
  const { restaurantId } = useParams();
  const [restaurant, setRestaurant] = useState(null);
  const [tables, setTables] = useState([]);

  useEffect(() => {
    const load = async () => {
      const [r, t] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/restaurants/${restaurantId}`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/tables/restaurant/${restaurantId}`, { withCredentials: true }),
      ]);
      setRestaurant(r.data);
      setTables(t.data.filter((x) => x.is_active));
      setTimeout(() => window.print(), 600);
    };
    load();
  }, [restaurantId]);

  if (!restaurant) return <div className="p-10">Yüklənir...</div>;

  return (
    <div className="p-8 bg-white">
      <style>{`@media print { .no-print { display: none !important; } @page { margin: 1cm; } }`}</style>
      <div className="no-print mb-4 flex items-center justify-between">
        <h1 className="text-2xl font-bold">{restaurant.name} — Masa QR kodları</h1>
        <button onClick={() => window.print()} className="px-4 py-2 bg-emerald-600 text-white rounded-xl font-semibold">🖨️ Yenidən çap et</button>
      </div>
      <div className="grid grid-cols-2 gap-6">
        {tables.map((table) => (
          <div key={table.id} className="border-2 border-stone-900 rounded-2xl p-5 break-inside-avoid">
            <div className="text-center">
              <p className="text-xs uppercase font-bold text-stone-600 tracking-wider">{restaurant.name}</p>
              <h2 className="text-5xl font-black text-stone-900 my-3">Masa #{table.number}</h2>
              {table.name && <p className="text-sm text-stone-600 mb-2">{table.name}</p>}
              <img src={table.qr_code} alt="QR" className="w-full max-w-[240px] mx-auto" />
              <p className="text-xs text-stone-500 mt-3">Menyu açmaq və sifariş vermək üçün QR-ni telefonla skan edin</p>
              <p className="text-[10px] text-stone-400 mt-1">SmartMenyu.az</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
